import {
  ApiResponse,
  LoginCredentials,
  RegisterData,
  User,
  Subscription,
  Chatbot,
  ChatMetrics,
  Invoice,
  PricingPlan,
  SubscriptionManagement,
  SubscriptionStats,
  PaymentMethod
} from '../types';

// Mock data
const mockUsers: User[] = [
  {
    id: '1',
    email: 'admin@example.com',
    name: 'Admin User',
    role: 'admin',
    createdAt: '2024-01-15T10:00:00Z',
    updatedAt: '2024-01-15T10:00:00Z',
  },
  {
    id: '2',
    email: 'user@example.com',
    name: 'Regular User',
    role: 'user',
    createdAt: '2024-02-20T10:00:00Z',
    updatedAt: '2024-02-20T10:00:00Z',
  },
];

const mockSubscription: Subscription = {
  id: 'sub_1',
  userId: '2',
  planId: 'plan_pro',
  plan: 'pro',
  status: 'active',
  startDate: '2024-01-01T00:00:00Z',
  endDate: '2024-12-31T23:59:59Z',
  autoRenew: true,
  price: 780000,
};

const mockChatbot: Chatbot = {
  id: 'bot_1',
  userId: '2',
  name: 'CS Assistant',
  tone: 'friendly',
  isActive: true,
  createdAt: '2024-01-01T00:00:00Z',
  updatedAt: '2024-01-15T10:00:00Z',
};

const mockMetrics: ChatMetrics = {
  totalChats: 1250,
  autoResolved: 980,
  humanTakeover: 270,
  averageResponseTime: 1.2,
  satisfactionRate: 92,
  monthlyChats: [450, 520, 480, 610, 590, 680, 720, 690, 750, 820, 780, 810],
};

const mockInvoices: Invoice[] = [
  {
    id: 'inv_1',
    name: 'aa',
    userId: '2',
    subscriptionId: 'sub_1',
    amount: 780000, // Updated to match Pro plan price
    status: 'paid',
    dueDate: '2024-02-01T00:00:00Z',
    paidDate: '2024-01-31T23:59:59Z',
    createdAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'inv_2',
    name: 'bb',
    userId: '2',
    subscriptionId: 'sub_1',
    amount: 780000, // Updated to match Pro plan price
    status: 'paid',
    dueDate: '2024-03-01T00:00:00Z',
    paidDate: '2024-02-28T23:59:59Z',
    createdAt: '2024-02-01T00:00:00Z',
  },
];

// Mock pricing plans
const mockPricingPlans: PricingPlan[] = [
  {
    id: 'plan_pro',
    name: 'Pro',
    price: 780000,
    description: 'Setara 1-2 Admin Tambahan',
    features: [
      '3.000 Maximum Active Users',
      '5 Human Agents',
      'Unlimited AI Agents',
      'Unlimited Inboxes',
      '15.000 AI Responses',
      'MarkeyAI Advanced AI Models',
      'Complete Documentation',
      'Free App Training',
      'Free Support',
    ],
    isActive: true,
    maxUsers: 3000,
    maxMessages: 15000,
    billingCycle: 'monthly',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'plan_business',
    name: 'Business',
    price: 1899000,
    description: 'Setara 3-5 Admin Tambahan',
    features: [
      '10.000 Maximum Active Users',
      '7 Human Agents',
      'Unlimited AI Agents',
      'Unlimited Inboxes',
      '50.000 AI Responses',
      'MarkeyAI Advanced AI Models',
      'Free Onboarding and Setup',
      'Dedicated Support',
      'Complete Documentation',
      'OpenAPI Access',
      'Free App Training',
      'Free 1 Account Whatsapp Whitelist',
    ],
    isActive: true,
    maxUsers: 10000,
    maxMessages: 50000,
    billingCycle: 'monthly',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'plan_enterprise',
    name: 'Enterprise',
    price: 2899000,
    description: 'Setara 7-10 Admin Tambahan',
    features: [
      '30.000 Maximum Active Users',
      '10 Human Agents',
      'Unlimited AI Agents',
      'Unlimited Inboxes',
      '150.000 AI Responses',
      'MarkeyAI Advanced AI Models',
      'Free Onboarding and Setup',
      'Dedicated Support',
      'Complete Documentation',
      'OpenAPI Access',
      'Free App Training',
      'Free 1 Account Whatsapp Whitelist',
    ],
    isActive: true,
    maxUsers: 30000,
    maxMessages: 150000,
    billingCycle: 'monthly',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'plan_unlimited_plus',
    name: 'Unlimited+',
    price: 7899000,
    description: 'Setara 20-30 Admin Tambahan',
    features: [
      'Unlimited Maximum Active Users',
      '30 Human Agents',
      'Unlimited AI Agents',
      'Unlimited Inboxes',
      '500.000 AI Responses',
      'MarkeyAI Advanced AI Models',
      'Free Onboarding and Setup',
      'Dedicated Support',
      'Complete Documentation',
      'OpenAPI Access',
      'Free App Training',
      '24/7 On Call Support',
      'Free 2 Account Whatsapp Whitelist',
    ],
    isActive: true,
    maxUsers: -1, // Unlimited
    maxMessages: 500000,
    billingCycle: 'monthly',
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
];

// Mock subscription management data
const mockSubscriptionManagements: SubscriptionManagement[] = [
  {
    id: 'sub_mgmt_1',
    userId: '2',
    planId: 'plan_pro',
    plan: mockPricingPlans[0], // Updated to use the new Pro plan
    status: 'active',
    startDate: '2024-01-01T00:00:00Z',
    endDate: '2024-12-31T23:59:59Z',
    autoRenew: true,
    price: 780000, // Updated price
    user: mockUsers[1],
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
  },
  {
    id: 'sub_mgmt_2',
    userId: '1',
    planId: 'plan_business',
    plan: mockPricingPlans[1], // Business plan
    status: 'active',
    startDate: '2024-02-01T00:00:00Z',
    endDate: '2025-01-31T23:59:59Z',
    autoRenew: true,
    price: 1899000, // Business plan price
    user: mockUsers[0],
    createdAt: '2024-02-01T00:00:00Z',
    updatedAt: '2024-02-01T00:00:00Z',
  },
];

// Mock subscription stats
const mockSubscriptionStats: SubscriptionStats = {
  totalSubscriptions: 2,
  activeSubscriptions: 2,
  inactiveSubscriptions: 0,
  cancelledSubscriptions: 0,
  monthlyRevenue: 2679000, // 780000 + 1899000
  yearlyRevenue: 32148000, // monthlyRevenue * 12
  planDistribution: {
    pro: 1,
    business: 1,
    enterprise: 0,
    unlimited_plus: 0,
  },
};

// Mock API functions
export const mockApiService = {
  // Auth endpoints
  async login(credentials: LoginCredentials): Promise<ApiResponse<{ user: User; token: string }>> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    const user = mockUsers.find(u => u.email === credentials.email);
    if (user && credentials.password === 'password') {
      return {
        success: true,
        data: {
          user,
          token: 'mock-jwt-token-' + Date.now(),
        },
      };
    }

    return {
      success: false,
      message: 'Invalid email or password',
    };
  },

  async register(data: RegisterData): Promise<ApiResponse<{ user: User; token: string }>> {
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Check if user already exists
    if (mockUsers.some(u => u.email === data.email)) {
      return {
        success: false,
        message: 'User with this email already exists',
      };
    }

    const newUser: User = {
      id: String(mockUsers.length + 1),
      email: data.email,
      name: data.name,
      role: 'user',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    mockUsers.push(newUser);

    return {
      success: true,
      data: {
        user: newUser,
        token: 'mock-jwt-token-' + Date.now(),
      },
    };
  },

  async forgotPassword(email: string): Promise<ApiResponse<any>> {
    await new Promise(resolve => setTimeout(resolve, 1000));
    return {
      success: true,
      message: 'Password reset email sent',
    };
  },

  async resetPassword(token: string, password: string): Promise<ApiResponse<any>> {
    await new Promise(resolve => setTimeout(resolve, 1000));
    return {
      success: true,
      message: 'Password reset successfully',
    };
  },

  // User endpoints
  async getCurrentUser(): Promise<ApiResponse<User>> {
    await new Promise(resolve => setTimeout(resolve, 500));

    // Return mock user based on stored user data
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        const user = JSON.parse(savedUser);
        // Find the user in mock data to ensure consistency
        const mockUser = mockUsers.find(u => u.id === user.id);
        if (mockUser) {
          return {
            success: true,
            data: mockUser,
          };
        }
      } catch (error) {
        console.error('Error parsing saved user:', error);
      }
    }

    return {
      success: false,
      message: 'No authenticated user',
    };
  },

  async updateProfile(data: Partial<User>): Promise<ApiResponse<User>> {
    await new Promise(resolve => setTimeout(resolve, 1000));

    const userIndex = mockUsers.findIndex(u => u.id === '2');
    if (userIndex !== -1) {
      mockUsers[userIndex] = { ...mockUsers[userIndex], ...data };
      return {
        success: true,
        data: mockUsers[userIndex],
      };
    }

    return {
      success: false,
      message: 'User not found',
    };
  },

  // Payment method endpoints
  async getPaymentMethod(): Promise<ApiResponse<PaymentMethod>> {
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Mock payment method data
    const mockPaymentMethod: PaymentMethod = {
      id: '01KD8N2VBAQ8SCDE228DJYDJ77',
      masked: '557338******1101',
    };
    
    return {
      success: true,
      data: mockPaymentMethod,
    };
  },

  // Subscription endpoints
  async getSubscription(): Promise<ApiResponse<Subscription>> {
    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      success: true,
      data: mockSubscription,
    };
  },

  async createSubscription(planId: string, paymentMethodId: string): Promise<ApiResponse<Subscription>> {
    await new Promise(resolve => setTimeout(resolve, 1500));

    const plan = mockPricingPlans.find(p => p.id === planId);
    if (!plan) {
      return {
        success: false,
        message: 'Plan not found',
      };
    }

    const updatedSubscription: Subscription = {
      ...mockSubscription,
      planId,
      plan: plan.name,
      status: 'active' as const,
    };

    return {
      success: true,
      data: updatedSubscription,
    };
  },

  async updateSubscription(planId: string): Promise<ApiResponse<Subscription>> {
    await new Promise(resolve => setTimeout(resolve, 1000));

    const plan = mockPricingPlans.find(p => p.id === planId);
    if (!plan) {
      return {
        success: false,
        message: 'Plan not found',
      };
    }

    const updatedSubscription: Subscription = {
      ...mockSubscription,
      planId,
      plan: plan.name,
    };

    return {
      success: true,
      data: updatedSubscription,
    };
  },

  async cancelSubscription(): Promise<ApiResponse<any>> {
    await new Promise(resolve => setTimeout(resolve, 1000));

    mockSubscription.status = 'cancelled';

    return {
      success: true,
      message: 'Subscription cancelled',
      data: { ...mockSubscription, status: 'cancelled' }
    };
  },

  async getSubscriptionPricingPlans(): Promise<ApiResponse<PricingPlan[]>> {
    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      success: true,
      data: mockPricingPlans,
    };
  },

  // Chatbot endpoints
  async getChatbot(): Promise<ApiResponse<Chatbot>> {
    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      success: true,
      data: mockChatbot,
    };
  },

  async updateChatbot(data: Partial<Chatbot>): Promise<ApiResponse<Chatbot>> {
    await new Promise(resolve => setTimeout(resolve, 1000));

    const updatedChatbot = { ...mockChatbot, ...data };

    return {
      success: true,
      data: updatedChatbot,
    };
  },

  // Dashboard endpoints
  async getMetrics(): Promise<ApiResponse<ChatMetrics>> {
    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      success: true,
      data: mockMetrics,
    };
  },

  // Invoice endpoints
  async getInvoices(): Promise<ApiResponse<Invoice[]>> {
    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      success: true,
      data: mockInvoices,
    };
  },

  async downloadInvoice(invoiceId: string): Promise<Blob> {
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Create a mock PDF blob
    const pdfContent = `Invoice ${invoiceId}\n\nAmount: Rp 780.000\nStatus: Paid\n\nThis is a mock PDF for demonstration purposes.`;
    return new Blob([pdfContent], { type: 'application/pdf' });
  },

  // Admin endpoints
  async getUsers(): Promise<ApiResponse<User[]>> {
    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      success: true,
      data: mockUsers,
    };
  },

  async updateUserStatus(userId: string, status: string): Promise<ApiResponse<User>> {
    await new Promise(resolve => setTimeout(resolve, 1000));

    const userIndex = mockUsers.findIndex(u => u.id === userId);
    if (userIndex !== -1) {
      // Note: In a real app, status would be a proper field
      // For demo, we'll just return the user
      return {
        success: true,
        data: mockUsers[userIndex],
      };
    }

    return {
      success: false,
      message: 'User not found',
    };
  },

  // Subscription Management endpoints
  async getSubscriptionManagements(): Promise<ApiResponse<SubscriptionManagement[]>> {
    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      success: true,
      data: mockSubscriptionManagements,
    };
  },

  async getPricingPlans(): Promise<ApiResponse<PricingPlan[]>> {
    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      success: true,
      data: mockPricingPlans,
    };
  },

  async createPricingPlan(plan: Omit<PricingPlan, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<PricingPlan>> {
    await new Promise(resolve => setTimeout(resolve, 1000));

    const newPlan: PricingPlan = {
      ...plan,
      id: 'plan_' + Date.now(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    mockPricingPlans.push(newPlan);

    return {
      success: true,
      data: newPlan,
    };
  },

  async updatePricingPlan(id: string, plan: Partial<PricingPlan>): Promise<ApiResponse<PricingPlan>> {
    await new Promise(resolve => setTimeout(resolve, 1000));

    const planIndex = mockPricingPlans.findIndex(p => p.id === id);
    if (planIndex !== -1) {
      mockPricingPlans[planIndex] = {
        ...mockPricingPlans[planIndex],
        ...plan,
        updatedAt: new Date().toISOString(),
      };
      return {
        success: true,
        data: mockPricingPlans[planIndex],
      };
    }

    return {
      success: false,
      message: 'Pricing plan not found',
    };
  },

  async deletePricingPlan(id: string): Promise<ApiResponse<any>> {
    await new Promise(resolve => setTimeout(resolve, 1000));

    const planIndex = mockPricingPlans.findIndex(p => p.id === id);
    if (planIndex !== -1) {
      mockPricingPlans.splice(planIndex, 1);
      return {
        success: true,
        message: 'Pricing plan deleted successfully',
      };
    }

    return {
      success: false,
      message: 'Pricing plan not found',
    };
  },

  async getSubscriptionStats(): Promise<ApiResponse<SubscriptionStats>> {
    await new Promise(resolve => setTimeout(resolve, 500));
    return {
      success: true,
      data: mockSubscriptionStats,
    };
  },

  async updateSubscriptionStatus(subscriptionId: string, status: string): Promise<ApiResponse<SubscriptionManagement>> {
    await new Promise(resolve => setTimeout(resolve, 1000));

    const subscriptionIndex = mockSubscriptionManagements.findIndex(s => s.id === subscriptionId);
    if (subscriptionIndex !== -1) {
      mockSubscriptionManagements[subscriptionIndex] = {
        ...mockSubscriptionManagements[subscriptionIndex],
        status: status as any,
        updatedAt: new Date().toISOString(),
      };
      return {
        success: true,
        data: mockSubscriptionManagements[subscriptionIndex],
      };
    }

    return {
      success: false,
      message: 'Subscription not found',
    };
  },
};